import * as _ from 'underscore';

import { CommonStyles } from '../../common/react/CommonStyles';
import { ColumnWidths } from './ColumnWidths';
import { IContentTypes } from './ContentTypes';

module PermissionsPanelLayoutValues {
  // Keep this column width in sync with $permissionsGridCellWidth in permissions.styl,
  // as well as capabilityColumnWidth in permissionsCellTemplates.pug
  export let CapabilityColumnWidth = 30;
  // The header of the permissions dialog requires at least this much space.
  // This number accounts for the width required for the add new rule row
  // across all languages, see defect 626426.
  export let MinDialogWidth = 920;

  export let MinColumnWidthsByField: _.Dictionary<number> = {
    granteeName: 200,
    permissionRole: 64
  };

  export let DefaultColumnWidthsByField: _.Dictionary<number> = {
    granteeName: 200,

    // Wide enough to fit the section header lock text in all languages
    permissionRole: 180
  };

  const ProjectColumnIndices: _.Dictionary<number[]> = {
    granteeName: [0],
    permissionRole: [2, 5, 10]
  };

  const ProjectColumnIndicesWithFlowsOrDataRolesOnly: _.Dictionary<number[]> = {
    granteeName: [0],
    permissionRole: [2, 5, 10, 14]
  };

  const ProjectColumnIndicesWithFlowsAndDataRoles: _.Dictionary<number[]> = {
    granteeName: [0],
    permissionRole: [2, 5, 10, 14, 18]
  };

  const NonProjectColumnIndices: _.Dictionary<number[]> = {
    granteeName: [0],
    permissionRole: [2]
  };

  const DatabaseColumnIndices: _.Dictionary<number[]> = {
    granteeName: [0],
    permissionRole: [2, 5]
  };

  const ColumnIndicesByContentType: IContentTypes<_.Dictionary<number[]>> = {
    project: ProjectColumnIndices,
    workbook: NonProjectColumnIndices,
    datasource: NonProjectColumnIndices,
    view: NonProjectColumnIndices,
    flow: NonProjectColumnIndices,
    dataRole: NonProjectColumnIndices,
    table: NonProjectColumnIndices,
    database: DatabaseColumnIndices,
  };

  export function getColumnIndicesByContentType(flowsEnabled: boolean, dataRolesEnabled: boolean): IContentTypes<_.Dictionary<number[]>> {
    let baseColumnIndices: IContentTypes<_.Dictionary<number[]>> = _.clone(ColumnIndicesByContentType);
    if (flowsEnabled && dataRolesEnabled) {
      baseColumnIndices.project = ProjectColumnIndicesWithFlowsAndDataRoles;
    } else if (flowsEnabled || dataRolesEnabled) {
      baseColumnIndices.project = ProjectColumnIndicesWithFlowsOrDataRolesOnly;
    }

    return baseColumnIndices;
  }
}

export interface PermissionsPanelColumnWidths extends ColumnWidths {
  /**
   * Returns the width, in pixels, that the permissions dialog should be for the given content type
   */
  getPermissionsDialogWidth: (viewportWidth: number, flowsEnabled: boolean, dataRolesEnabled: boolean) => number;
}

export function getPermissionsPanelColumnWidths(
  contentType: string,
  flowsEnabled: boolean,
  dataRolesEnabled: boolean,
): PermissionsPanelColumnWidths {
  return new PermissionsPanelColumnWidthsImpl(
    contentType,
    PermissionsPanelLayoutValues.MinColumnWidthsByField,
    PermissionsPanelLayoutValues.DefaultColumnWidthsByField,
    PermissionsPanelLayoutValues.getColumnIndicesByContentType(flowsEnabled, dataRolesEnabled),
    PermissionsPanelLayoutValues.CapabilityColumnWidth,
    PermissionsPanelLayoutValues.MinDialogWidth
  );
}

export class PermissionsPanelColumnWidthsImpl extends ColumnWidths implements PermissionsPanelColumnWidths {

  constructor(private contentType: string,
              private MinColumnWidthsByField: _.Dictionary<number>,
              private DefaultColumnWidthsByField: _.Dictionary<number>,
              private ColumnIndicesByContentType: IContentTypes<_.Dictionary<number[]>>,
              private CapabilityColumnWidth: number,
              private MinDialogWidth: number) {
    super();
    const minWidths: number[] = this.orderedWidthsFromFieldNames(this.MinColumnWidthsByField);
    this.setMinWidths(minWidths);
  }

  private orderedWidthsFromFieldNames(widthsByField: any): number[] {
    const widths: number[] = [];

    const columnIndices: _.Dictionary<number[]> = this.ColumnIndicesByContentType[this.contentType];
    _.each(widthsByField, (width: number, fieldName: string) => {
      _.each(columnIndices[fieldName], (index: number) => {
        widths[index] = width;
      });
    });

    return widths;
  }

  /**
   * Sets the preferred widths for each field type to the maximum of the measured widths for that
   * field and its default width (overrides superclass method)
   */
  public setPreferredWidths(measuredWidths: number[], containerWidth?: number) {
    const preferredColumnWidths = this.getPreferredColumnWidths(measuredWidths);
    super.setPreferredWidths(preferredColumnWidths, containerWidth);
  }

  private getPreferredColumnWidths(measuredWidths: number[]): number[] {
    const preferredWidthsByField: _.Dictionary<number> = {};

    const columnIndicesByField = this.ColumnIndicesByContentType[this.contentType];
    _.each(columnIndicesByField, (indices: number[], fieldName: string) => {
      const measuredWidthValues = _.map(indices, (index: number) => measuredWidths[index]);
      const preferredWidth = _.max(measuredWidthValues.concat(this.DefaultColumnWidthsByField[fieldName]));
      preferredWidthsByField[fieldName] = preferredWidth;
    });

    return this.orderedWidthsFromFieldNames(preferredWidthsByField);
  }

  public getPermissionsDialogWidth(viewportWidth: number, flowsEnabled: boolean, dataRolesEnabled: boolean): number {
    const widthsByContentType = this.calculatePermissionsDialogWidths(flowsEnabled, dataRolesEnabled);
    const padding = 2 * CommonStyles.Sizes.LargeSpace;
    const effectiveViewportWidth = Math.max(viewportWidth, CommonStyles.Sizes.MinSupportedAppWidth) - padding;
    return Math.min(Math.max(widthsByContentType[this.contentType], this.MinDialogWidth), effectiveViewportWidth);
  }

  private calculatePermissionsDialogWidths(flowsEnabled: boolean, dataRolesEnabled: boolean): IContentTypes<number> {
    const capabilitySectionSpacing = 14;
    const scrollBarWidth = 20;
    // The column dividers are drag targets, which have extra hidden width beyond the visible divider
    // (see dragTarget.styl). Allocate extra space in the dialog for this width so we don't get a
    // horizontal scroll bar with the dialog's default width.
    const extraSpaceForRightmostDragTarget = 2;
    const columnDividerWidth = 3;
    const fixedColumnsTotalWidth = sum(this.DefaultColumnWidthsByField) + scrollBarWidth + extraSpaceForRightmostDragTarget;
    const projectRoleColumnIndices = PermissionsPanelLayoutValues.getColumnIndicesByContentType(flowsEnabled, dataRolesEnabled)
      .project.permissionRole;
    const numProjectRoleColumns = projectRoleColumnIndices.length - 1;
    const numDatabaseRoleColumns = PermissionsPanelLayoutValues.getColumnIndicesByContentType(flowsEnabled, dataRolesEnabled)
      .database.permissionRole.length - 1;
    return {
      // The project permissions size equals the workbook permissions (the largest section),
      // plus 2 or 3 extra role columns and their corresponding dividers
      project:  (14 * this.CapabilityColumnWidth) + (3 * capabilitySectionSpacing) + fixedColumnsTotalWidth +
                (numProjectRoleColumns * this.DefaultColumnWidthsByField['permissionRole']) +
                ((numProjectRoleColumns + 2) * columnDividerWidth),
      workbook: (14 * this.CapabilityColumnWidth) + (3 * capabilitySectionSpacing) + fixedColumnsTotalWidth +
                (2 * columnDividerWidth),
      view: (11 * this.CapabilityColumnWidth) + (3 * capabilitySectionSpacing) + fixedColumnsTotalWidth +
            (2 * columnDividerWidth),
      datasource: (6 * this.CapabilityColumnWidth) + (2 * capabilitySectionSpacing) + fixedColumnsTotalWidth +
                  (2 * columnDividerWidth),
      flow: (7 * this.CapabilityColumnWidth) + (2 * capabilitySectionSpacing) + fixedColumnsTotalWidth +
            (2 * columnDividerWidth),
      dataRole: (5 * this.CapabilityColumnWidth) + (2 * capabilitySectionSpacing) + fixedColumnsTotalWidth +
                (2 * columnDividerWidth),
      database: (3 * this.CapabilityColumnWidth) + (2 * capabilitySectionSpacing) + fixedColumnsTotalWidth +
                ((numDatabaseRoleColumns + 2) * columnDividerWidth) +
                (numDatabaseRoleColumns * this.DefaultColumnWidthsByField['permissionRole']),
      table: (3 * this.CapabilityColumnWidth) + (1 * capabilitySectionSpacing) + fixedColumnsTotalWidth +
             (2 * columnDividerWidth)
    };
  }

}

function sum(values: _.Dictionary<number>): number {
  return _.reduce(values, (memo: number, num: number) => memo + num);
}
